import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import ACTION_TYPE from '../../store/ACTION_TYPE';
import createReducerFunction from '../../store/reducerCreator';
import { parseAppDomainTokens } from '../../components/parseAppsDomainTokens';

interface App {
    appId: string;
    appLogo: string;
    description: string;
    domainSettings: string;
    domainSettingsRequired: boolean;
    featuredOrder: number;
    isNew: boolean;
    name: string;
    sideloaded: boolean;
    supportedBy: string;
    type: string;
    isProtectConfigurable: boolean;
}

interface State {
    apps: App[];
    isFetching: boolean;
    shouldUpdate: boolean;
    search: string;
    filters: {
        [key: string]: {
            [key: string]: boolean;
        };
        platform: {
            connect: boolean;
            protect: boolean;
        };
        type: {
            new: boolean;
            egnyte_ui: boolean;
            web: boolean;
            egnyte_apps: boolean;
            custom: boolean;
        };
        status: {
            admin_notEnabled: boolean;
            admin_allowedToUsers: boolean;
            admin_addedToUsers: boolean;
            admin_addedForGroups: boolean;
            user_added: boolean;
            user_addAvailable: boolean;
            user_notEnabled: boolean;
        };
        categories: {
            analytics: boolean;
            casb: boolean;
            chat: boolean;
            coEditing: boolean;
            collaboration: boolean;
            crm: boolean;
            email: boolean;
            editing: boolean;
            esignature: boolean;
            forms: boolean;
            migration: boolean;
            mobile: boolean;
            projectManagement: boolean;
            sales: boolean;
            security: boolean;
            sso: boolean;
            workflow: boolean;
            'certified-desktop-apps': boolean;
        };
        industries: {
            construction: boolean;
            financialServices: boolean;
            healthcareAndLifeSciences: boolean;
            legal: boolean;
            mediaAndAdvertising: boolean;
        };
    };
    expandedFilters: string[];
    expanded: boolean;
    sequence: number;
    queryParamPlatform: string;
    isFirstTime: boolean;
    isInitialLoading: boolean;
    isFirstTimeUnifiedPlatform: boolean;
}

export const initialState: State = {
    apps: [],
    isFetching: false,
    shouldUpdate: false,
    search: '',
    filters: {
        platform: {
            connect: false,
            protect: false,
        },
        type: {
            new: false,
            egnyte_ui: false,
            web: false,
            egnyte_apps: false,
            custom: false,
        },
        status: {
            admin_notEnabled: false,
            admin_allowedToUsers: false,
            admin_addedToUsers: false,
            admin_addedForGroups: false,
            user_added: false,
            user_addAvailable: false,
            user_notEnabled: false,
        },
        categories: {
            analytics: false,
            casb: false,
            chat: false,
            coEditing: false,
            collaboration: false,
            crm: false,
            email: false,
            editing: false,
            esignature: false,
            forms: false,
            migration: false,
            mobile: false,
            projectManagement: false,
            sales: false,
            security: false,
            sso: false,
            workflow: false,
            'certified-desktop-apps': false,
        },
        industries: {
            construction: false,
            financialServices: false,
            healthcareAndLifeSciences: false,
            legal: false,
            mediaAndAdvertising: false,
        },
    },
    expandedFilters: ['platform', 'type', 'status'],
    expanded: false,
    sequence: -1,
    queryParamPlatform: '',
    isFirstTime: true,
    isInitialLoading: true,
    isFirstTimeUnifiedPlatform: true,
};

interface Action {
    type:
        | ACTION_TYPE.REQUEST_APPS_PENDING
        | ACTION_TYPE.REQUEST_APPS_FULFILLED
        | ACTION_TYPE.REQUEST_APPS_REJECTED
        | ACTION_TYPE.TOGGLE_ADMIN_MODE
        | ACTION_TYPE.APP_SETTINGS_CHANGED_FULFILLED
        | ACTION_TYPE.APP_SETTINGS_CHANGED_REJECTED
        | ACTION_TYPE.UPDATE_APPS_FILTER
        | ACTION_TYPE.REPLACE_APPS_FILTER
        | ACTION_TYPE.SELECT_NEW_FILTER
        | ACTION_TYPE.REFRESH_APPS_FILTER
        | ACTION_TYPE.PLATFORM_SELECT
        | ACTION_TYPE.SEARCH_CHANGE
        | ACTION_TYPE.RESET_APPS_FILTERS
        | ACTION_TYPE.TOGGLE_APPS_FILTERS
        | ACTION_TYPE.REUPLOAD_SIDELOADED_APP_FULFILLED
        | ACTION_TYPE.ADD_SIDELOADED_APP_FULFILLED
        | ACTION_TYPE.SET_STATE_QUERY_PARAM_PLATFORM;
    payload: App[];
    meta: {
        sequence: number;
        pageDomain: string;
    };
    filterGroup: string;
    filter: {
        [key: string]: boolean;
    };
    platform: string;
    query: string;
}

export default createReducerFunction<State, Action>({
    initialState: cloneDeep(initialState),
    reducers: {
        [ACTION_TYPE.REQUEST_APPS_PENDING]: (state) => ({ ...state, isFetching: true, shouldUpdate: false }),
        [ACTION_TYPE.REQUEST_APPS_FULFILLED]: (state, { payload, meta }) => {
            if (state.sequence > meta.sequence) {
                return state;
            }
            const appsWithDomainUrls = parseAppDomainTokens(payload, meta.pageDomain);

            return {
                ...state,
                sequence: meta.sequence,
                isFetching: false,
                shouldUpdate: false,
                isFirstTime: false,
                apps: appsWithDomainUrls || payload,
            };
        },
        [ACTION_TYPE.REQUEST_APPS_REJECTED]: (state) => ({ ...state, isFetching: false, shouldUpdate: false }),

        [ACTION_TYPE.TOGGLE_ADMIN_MODE]: handleToggleAdmin,
        [ACTION_TYPE.APP_SETTINGS_CHANGED_FULFILLED]: handleAppActionFullfilled,
        [ACTION_TYPE.APP_SETTINGS_CHANGED_REJECTED]: handleAppActionRejected,
        [ACTION_TYPE.UPDATE_APPS_FILTER]: (state, { filterGroup, filter }) =>
            produce(state, (draft) => {
                draft.filters[filterGroup] = {
                    ...draft.filters[filterGroup],
                    ...filter,
                };
                draft.shouldUpdate = true;
                draft.isFirstTimeUnifiedPlatform = false;
            }),
        [ACTION_TYPE.REPLACE_APPS_FILTER]: (state, { filterGroup, filter }) => ({
            ...state,
            filters: {
                ...initialState.filters,
                [filterGroup]: { ...initialState.filters[filterGroup], ...filter },
            },
            search: initialState.search,
            shouldUpdate: true,
        }),
        [ACTION_TYPE.SELECT_NEW_FILTER]: (state) =>
            produce(state, (draft) => {
                draft.filters.age = {
                    ...draft.filters.age,
                    new: true,
                };
                draft.shouldUpdate = true;
            }),
        [ACTION_TYPE.REFRESH_APPS_FILTER]: (state) =>
            produce(state, (draft) => {
                draft.shouldUpdate = true;
            }),
        [ACTION_TYPE.PLATFORM_SELECT]: (state, { platform }) =>
            produce(state, (draft) => {
                draft.filters.platform = {
                    ...initialState.filters.platform,
                    [platform]: true,
                };
                draft.isInitialLoading = false;
            }),
        [ACTION_TYPE.SEARCH_CHANGE]: (state, { query }) => ({ ...state, search: query }),
        [ACTION_TYPE.RESET_APPS_FILTERS]: (state) => ({
            ...state,
            filters: initialState.filters,
            search: initialState.search,
            shouldUpdate: true,
        }),
        [ACTION_TYPE.TOGGLE_APPS_FILTERS]: (state) => ({ ...state, expanded: !state.expanded }),
        [ACTION_TYPE.REUPLOAD_SIDELOADED_APP_FULFILLED]: (state) => forceUpdateAppList(state),
        [ACTION_TYPE.ADD_SIDELOADED_APP_FULFILLED]: (state) => forceUpdateAppList(state),
        [ACTION_TYPE.SET_STATE_QUERY_PARAM_PLATFORM]: (state, { platform }) =>
            produce(state, (draft) => {
                draft.queryParamPlatform = platform;
            }),
    },
});

function handleToggleAdmin(state: State) {
    return [forceUpdateAppList, resetUserRelatedFilters].reduce(
        (resultState, stateMapper) => stateMapper(resultState),
        state
    );
}

function handleAppActionFullfilled(state: State) {
    return forceUpdateAppList(state);
}

function handleAppActionRejected(state: State) {
    return forceUpdateAppList(state);
}

function forceUpdateAppList(state: State) {
    return Object.assign(cloneDeep(state), {
        shouldUpdate: true,
    });
}

function resetUserRelatedFilters(state: State) {
    const result = cloneDeep(state);
    result.filters.status = { ...initialState.filters.status, ...state.filters.status };

    return result;
}
